import React, { useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Chip,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useFAQs, useDeleteFAQ } from '../hooks/useFAQApi';
import { FAQEditor } from './FAQEditor';
import type { FAQ, FAQFilters } from '../types/faq.types';

export const FAQList: React.FC = () => {
  const [filters, setFilters] = useState<FAQFilters>({
    search: '',
    sortBy: 'created_at',
    sortOrder: 'desc',
  });
  const [page, setPage] = useState(1);
  const [editingFAQ, setEditingFAQ] = useState<FAQ | null>(null);

  const { data: faqData, isLoading, error } = useFAQs(filters, page);
  const deleteFAQMutation = useDeleteFAQ();

  const handleFilterChange = (field: keyof FAQFilters, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(1); // Reset to first page when filtering
  };

  const handleDeleteFAQ = async (id: number) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette FAQ ?')) {
      try {
        await deleteFAQMutation.mutateAsync(id);
      } catch (error) {
        // Error handled by the mutation
      }
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Erreur lors du chargement des FAQs: {error.message}
      </Alert>
    );
  }

  const faqs = faqData?.results || [];

  return (
    <Box>
      {/* Filters */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Rechercher"
          variant="outlined"
          size="small"
          value={filters.search}
          onChange={(e) => handleFilterChange('search', e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
          }}
          sx={{ flexGrow: 1, minWidth: 250 }}
        />

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Trier par</InputLabel>
          <Select
            value={filters.sortBy}
            label="Trier par"
            onChange={(e) => handleFilterChange('sortBy', e.target.value)}
          >
            <MenuItem value="created_at">Date de création</MenuItem>
            <MenuItem value="updated_at">Dernière modification</MenuItem>
            <MenuItem value="question">Question</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Ordre</InputLabel>
          <Select
            value={filters.sortOrder}
            label="Ordre"
            onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
          >
            <MenuItem value="asc">Croissant</MenuItem>
            <MenuItem value="desc">Décroissant</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* FAQ Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Question</TableCell>
              <TableCell>Réponse</TableCell>
              <TableCell>Créée le</TableCell>
              <TableCell>Modifiée le</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {faqs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary" py={4}>
                    {filters.search ? 'Aucune FAQ trouvée pour cette recherche' : 'Aucune FAQ créée'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              faqs.map((faq: FAQ) => (
                <TableRow key={faq.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 300 }}>
                      {faq.question.length > 100
                        ? `${faq.question.substring(0, 100)}...`
                        : faq.question
                      }
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 300 }}>
                      {faq.answer.length > 100
                        ? `${faq.answer.substring(0, 100)}...`
                        : faq.answer
                      }
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(faq.created_at).toLocaleDateString('fr-FR')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(faq.updated_at).toLocaleDateString('fr-FR')}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => setEditingFAQ(faq)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteFAQ(faq.id)}
                        color="error"
                        disabled={deleteFAQMutation.isPending}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Modal */}
      {editingFAQ && (
        <FAQEditor
          open={!!editingFAQ}
          faq={editingFAQ}
          onClose={() => setEditingFAQ(null)}
          mode="edit"
        />
      )}
    </Box>
  );
};
